import { useEffect, useReducer, useRef, useState, type ComponentType } from 'react';
import { useNavigate } from 'react-router';
import { NotificationTextMessage } from '@/core/enums/notification';
import type { SelectedClient, SideListItem } from '@/core/models/practice.models';
import type { EmailSendResponse, ReportRow, SendEmailDialogData } from '@/core/models/reports.models';
import { notifyBlobError, notifyError, notifySuccess } from '@/core/notify/notify';
import { selectedClient } from '@/core/selectedClient';
import { practiceService } from '@/core/services/practice';
import { reportsService } from '@/core/services/reports';
import { ClosePopup } from '@/kit/ClosePopup';
import { ConfirmPopup } from '@/kit/ConfirmPopup';
import { openDialog, type DialogComponentProps } from '@/kit/dialog';
import { REPORT_NOT_PRODUCIBLE } from './accountsFiling';
import { REPORT_PDF_PREVIEW_DIALOG, ReportPdfPreview } from './ReportPdfPreview';
import { REPORT_SOURCE_LINEAGE_DIALOG, ReportSourceLineageDialog } from './ReportSourceLineageDialog';
import { SendTrialBalance } from './SendTrialBalance';
import type { ReportOptionsDialogData } from './TaxreturnReportsettingsOption';
import { TrialBalancePreview } from './TrialBalancePreview';

interface AccountsReportListOptions {
  /** 0 Annual, 1 Management (ReportTypeCode). */
  reportType: number;
  /** Used in the email dialog titles. */
  reportTitle: string;
  /** #169: called after reports are deleted and before the list reloads, with the pre-delete rows. */
  onReportsDeleted?: (client: SelectedClient, rows: ReportRow[]) => void;
}

/**
 * List behaviour shared by the Annual Accounts and Management Accounts report screens (Angular AccountsReportList):
 * selected-client guard, load, checkbox selection, add/edit navigation, trial balance and PDF previews, single/bulk
 * download, delete and email, single-row regenerate, and the report options dialog.
 *
 * The selection is a mutable Set (like Angular's) so an action always reads the state at the moment it runs.
 */
export function useAccountsReportList({ reportType, reportTitle, onReportsDeleted }: AccountsReportListOptions) {
  const navigate = useNavigate();
  const [client] = useState<SelectedClient | null>(() => selectedClient());
  const [dataSource, setDataSourceState] = useState<ReportRow[]>([]);
  /** The first load has finished (rows or an error): only then is an empty list "empty" rather than still loading. */
  const [isLoaded, setIsLoaded] = useState(false);
  // Fetched on every init as Angular did, although no template renders a client picker any more (#157).
  const [, setClients] = useState<SideListItem[]>([]);
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  const [selection] = useState(() => new Set<string>());
  const rows = useRef<ReportRow[]>([]);
  const mounted = useRef(true);
  const deletedHook = useRef(onReportsDeleted);
  useEffect(() => {
    deletedHook.current = onReportsDeleted;
  });

  const setDataSource = (next: ReportRow[]) => {
    rows.current = next;
    setDataSourceState(next);
  };

  const applyRows = (loaded: ReportRow[]) => {
    if (!mounted.current) return;
    setIsLoaded(true);
    const sorted = [...loaded].sort((a, b) => {
      const aIsAA = /^AA-\d+/i.test(a.refNo || '');
      const bIsAA = /^AA-\d+/i.test(b.refNo || '');
      if (aIsAA !== bIsAA) return aIsAA ? -1 : 1;
      return 0;
    });
    setDataSource(sorted);
    Array.from(selection).forEach((id) => {
      if (!sorted.some((r) => r.id === id)) {
        selection.delete(id);
      }
    });
    rerender();
  };

  const onLoadError = (error: unknown) => {
    if (!mounted.current) return;
    setIsLoaded(true);
    notifyError(error);
  };

  const load = async () => {
    if (!client) {
      return;
    }
    await reportsService.getReports(client.id, reportType).then(applyRows, onLoadError);
  };

  useEffect(() => {
    mounted.current = true;
    if (!client) {
      notifyError('Select a client from Accounts first.');
      navigate('/home/accountclient');
      return () => {
        mounted.current = false;
      };
    }
    practiceService.getClientSideList(1, 1000, '').then(
      (response) => {
        if (!mounted.current) return;
        const list = response.sideModuleList;
        if (!list.some((c) => c.id === client.id)) {
          list.unshift({ id: client.id, universalId: client.id, name: client.name, value: null });
        }
        setClients(list);
      },
      (error) => {
        if (mounted.current) notifyError(error);
      },
    );
    reportsService.getReports(client.id, reportType).then(applyRows, onLoadError);
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAllSelected = () => dataSource.length > 0 && dataSource.every((r) => selection.has(r.id));
  const isPartlySelected = () => selection.size > 0 && !isAllSelected();

  const toggleAll = (checked: boolean) => {
    selection.clear();
    if (checked) {
      dataSource.forEach((r) => selection.add(r.id));
    }
    rerender();
  };

  const toggleRow = (row: ReportRow, checked: boolean) => {
    if (checked) {
      selection.add(row.id);
    } else {
      selection.delete(row.id);
    }
    rerender();
  };

  const onAddnewReport = () => navigate(`/home/taxreturns/addnewreport?type=${reportType}`);

  const onEditReport = (row: ReportRow) => navigate(`/home/taxreturns/addnewreport?type=${reportType}&id=${row.id}`);

  /** A read-only look at the trial balance's figures, without leaving the report list. */
  const onTrialBalanceClick = (row: ReportRow) => {
    openDialog(TrialBalancePreview, {
      // #161: both trial balances the report lists, not just the current one.
      data: { trialBalanceId: row.currentTrialBalanceId, previousTrialBalanceId: row.previousTrialBalanceId },
    });
  };

  /** The report's PDF, shown inline. Editing the report itself is still reached via onEditReport(). */
  const previewReport = (row: ReportRow) => {
    if (row.canProduce === false) {
      notifyError(row.blockedReason ?? REPORT_NOT_PRODUCIBLE);
      return;
    }
    openDialog(ReportPdfPreview, {
      data: {
        reportId: row.id,
        refNo: row.refNo,
        // The old application's preview offers Email and Regenerate for the report on show.
        onEmail: () => emailOne(row, SendTrialBalance),
        onRegenerate: () => regenerate(row),
      },
      ...REPORT_PDF_PREVIEW_DIALOG,
    });
  };

  /**
   * APT342: where this report's figures came from - Source TB, mapping, adjustment journals, Adjusted TB and its version - with the
   * history of every generation and, when journals were posted since, the warning and this list's Regenerate.
   */
  const viewSource = (row: ReportRow) => {
    openDialog(ReportSourceLineageDialog, {
      data: { reportId: row.id, refNo: row.refNo, onRegenerate: () => regenerate(row) },
      ...REPORT_SOURCE_LINEAGE_DIALOG,
    });
  };

  const openReportOption = (optionsDialog: ComponentType<DialogComponentProps<ReportOptionsDialogData, boolean>>) => {
    if (client) {
      // #176: the options belong to this list's report type (Annual or Management), not to the client as a whole.
      openDialog(optionsDialog, { data: { clientId: client.id, reportType } });
    }
  };

  /** The ticked rows when this row is one of them (and more than one is ticked), otherwise just this row. */
  const idsFor = (row: ReportRow): string[] =>
    selection.has(row.id) && selection.size > 1 ? Array.from(selection) : [row.id];

  /**
   * prod-p3: a report whose trial balance was deleted has no figures left to render. The row stays in the list as the
   * firm's record, but producing it can only fail, so say why instead of sending the request. A blocked row without a
   * reason lets the action proceed (Angular quirk).
   */
  const firstBlockedReason = (ids: string[]): string | null => {
    const blocked = rows.current.find((r) => ids.includes(r.id) && r.canProduce === false);
    return blocked?.blockedReason ?? null;
  };

  const download = (row: ReportRow) => downloadIds(idsFor(row));

  const downloadIds = async (ids: string[]) => {
    const blocked = firstBlockedReason(ids);
    if (blocked) {
      notifyError(blocked);
      return;
    }
    try {
      if (ids.length > 1) {
        await reportsService.bulkDownloadReports(ids);
      } else {
        await reportsService.downloadReport(ids[0]);
      }
    } catch (error) {
      // A refused download returns its reason as a blob (trial balance deleted, report not found ...).
      notifyBlobError(error, 'The report could not be downloaded.');
    }
  };

  const openDeleteClick = (row: ReportRow) => deleteIds(idsFor(row));

  const deleteIds = async (ids: string[]) => {
    const confirmed = await openDialog(ClosePopup, {
      data: { itemName: ids.length > 1 ? 'Reports' : 'Report', count: ids.length },
    }).afterClosed();
    if (!confirmed) {
      return;
    }
    const deleted = rows.current.filter((r) => ids.includes(r.id));
    try {
      await reportsService.deleteReports(ids);
    } catch (error) {
      notifyError(error);
      return;
    }
    notifySuccess(NotificationTextMessage.recordDeletedSuccessfully);
    ids.forEach((id) => selection.delete(id));
    if (client) deletedHook.current?.(client, deleted);
    void load();
  };

  /** Sends every ticked report, reusing the single-report dialog for the covering message. */
  const emailSelected = async (
    row: ReportRow,
    ids: string[],
    sendDialog: ComponentType<DialogComponentProps<SendEmailDialogData, boolean>>,
  ) => {
    try {
      const draft = await reportsService.getReportEmailDraft(row.id);
      const data: SendEmailDialogData = {
        title: `Send ${ids.length} ${reportTitle} reports`,
        draft,
        send: async (payload) => {
          // The draft is the clicked report's (its ref. no. and period); each report keeps its own subject and body unless
          // the preparer changed them, which then goes to every report.
          const result = await reportsService.bulkEmailReports({
            reportIds: ids,
            to: payload.to,
            cc: payload.cc ?? null,
            bcc: payload.bcc ?? null,
            subject: payload.subject.trim() === draft.subject.trim() ? null : payload.subject,
            body: (payload.body ?? '') === (draft.body ?? '') ? null : payload.body,
          });
          const failures = (result.results ?? [])
            .filter((r) => !r.sent)
            .map((r) => `${rows.current.find((x) => x.id === r.reportId)?.refNo ?? r.reportId}: ${r.message}`);
          if (result.sent === 0) {
            // Nothing went, so the dialog stays open and Send can safely be tried again.
            throw new Error(`No reports were sent. ${failures.join(' ')}`.trim());
          }
          // Some went: the dialog still closes, so a second Send cannot email those reports again, and says which did not.
          return {
            emailLogId: result.results?.[0]?.emailLogId ?? '',
            status: failures.length > 0 ? 'Partial' : 'Sent',
            message:
              failures.length > 0
                ? `${result.sent} of ${result.requested} reports sent; ${result.failed} not sent. ${failures.join(' ')}`
                : `${result.sent} of ${result.requested} reports sent.`,
          } as EmailSendResponse;
        },
        downloadAttachment: () => reportsService.bulkDownloadReports(ids),
      };
      openDialog(sendDialog, { data });
    } catch (error) {
      notifyError(error);
    }
  };

  /**
   * Reruns the document build for this one report against its current trial balance/report settings - nothing is
   * ever cached, so this both confirms the report can still be produced and refreshes its "Last Updated" column.
   * Unlike download/email this has no bulk endpoint, so it always acts on just the clicked row, even when others
   * are ticked too.
   */
  const regenerate = async (row: ReportRow): Promise<boolean> => {
    if (row.canProduce === false) {
      notifyError(row.blockedReason ?? REPORT_NOT_PRODUCIBLE);
      return false;
    }
    // BUG-050: the old app asks before it rebuilds the report.
    const confirmed = await openDialog(ConfirmPopup, {
      data: {
        title: 'Regenerate report',
        message: `Regenerate ${row.refNo} from ${row.balanceSource === 1 ? 'the latest adjusted trial balance' : 'its trial balance'} now?`,
      },
    }).afterClosed();
    if (!confirmed) {
      return false;
    }
    try {
      const updated = await reportsService.regenerateReport(row.id);
      notifySuccess(`${updated.refNo} has been regenerated.`);
    } catch (error) {
      notifyError(error);
      return false;
    }
    void load();
    return true;
  };

  const email = async (
    row: ReportRow,
    sendDialog: ComponentType<DialogComponentProps<SendEmailDialogData, boolean>>,
  ) => {
    const ids = idsFor(row);
    const blocked = firstBlockedReason(ids);
    if (blocked) {
      notifyError(blocked);
      return;
    }
    if (ids.length > 1) {
      await emailSelected(row, ids, sendDialog);
      return;
    }
    await emailOne(row, sendDialog);
  };

  /** Emails just this report, whatever else is ticked (the preview's Email button and a single row). */
  const emailOne = async (
    row: ReportRow,
    sendDialog: ComponentType<DialogComponentProps<SendEmailDialogData, boolean>>,
  ) => {
    try {
      const draft = await reportsService.getReportEmailDraft(row.id);
      const data: SendEmailDialogData = {
        title: `Send ${reportTitle}`,
        draft,
        send: (payload) => reportsService.sendReport(row.id, payload),
        downloadAttachment: () => reportsService.downloadReport(row.id),
      };
      openDialog(sendDialog, { data });
    } catch (error) {
      notifyError(error);
    }
  };

  /** The selection bar's actions: every ticked report. */
  const ticked = () => rows.current.filter((r) => selection.has(r.id)).map((r) => r.id);
  const deleteSelected = () => deleteIds(ticked());
  const downloadSelected = () => downloadIds(ticked());
  const clearSelection = () => toggleAll(false);

  return {
    client,
    dataSource,
    isLoaded,
    selection,
    deleteSelected,
    downloadSelected,
    clearSelection,
    load,
    isAllSelected,
    isPartlySelected,
    toggleAll,
    toggleRow,
    onAddnewReport,
    onEditReport,
    onTrialBalanceClick,
    previewReport,
    viewSource,
    openReportOption,
    idsFor,
    firstBlockedReason,
    download,
    openDeleteClick,
    email,
    regenerate,
  };
}

export type AccountsReportListApi = ReturnType<typeof useAccountsReportList>;
